import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, SectionTitle } from "../components/shared";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Landmark, Building2, Globe2, CircleCheck, CirclePlus, Pencil, Trash2, Plus } from "lucide-react";

const NIVELES = [
  { key: "municipal", label: "Municipal", icon: Building2, sub: "Municipio de Solidaridad — Playa del Carmen" },
  { key: "estatal", label: "Estatal", icon: Landmark, sub: "Estado de Quintana Roo" },
  { key: "federal", label: "Federal", icon: Globe2, sub: "SEMARNAT, CONAGUA, INAH, CFE, IMSS, COFEPRIS..." },
];

export default function Catalogo() {
  const { pid } = useParams();
  const { canEdit, isAdmin } = useAuth();
  const [catalogo, setCatalogo] = useState(null);
  const [upes, setUpes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [aplicar, setAplicar] = useState(null); // permiso del catálogo
  const [upeSel, setUpeSel] = useState("");
  const [editor, setEditor] = useState(null); // {id?, nombre, autoridad, nivel, dependencia, nota, prioridad, requisitosTxt}
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.get("/catalogo"), api.get(`/properties/${pid}/upes`), api.get(`/properties/${pid}/tramites`),
  ]).then(([c, u, t]) => { setCatalogo(c.data); setUpes(u.data); setTramites(t.data); });
  useEffect(() => { setCatalogo(null); load(); }, [pid]); // eslint-disable-line

  const instanciados = useMemo(() => {
    const m = {};
    tramites.filter((t) => t.catalogo_id).forEach((t) => { (m[t.catalogo_id] = m[t.catalogo_id] || []).push(t); });
    return m;
  }, [tramites]);

  const semaforoAutoridad = useMemo(() => {
    const m = {};
    tramites.filter((t) => t.categoria?.startsWith("Permiso") || t.nivel !== "otro").forEach((t) => {
      const a = (t.autoridad || "Otro").split("/")[0].split("(")[0].trim();
      m[a] = m[a] || { total: 0, aprobados: 0, detenidos: 0 };
      m[a].total++;
      if (t.estatus === "Aprobado") m[a].aprobados++;
      if (["En prevención/subsanación", "Vencido", "Rechazado"].includes(t.estatus)) m[a].detenidos++;
    });
    return m;
  }, [tramites]);

  const aplicarUpe = async () => {
    if (!upeSel) return toast.error("Selecciona una UPE");
    try {
      const r = await api.post(`/upes/${upeSel}/permisologia`, { catalogo_ids: [aplicar.id] });
      toast.success(r.data.creados > 0 ? "Permiso instanciado en la UPE" : "Este permiso ya estaba instanciado en esa UPE");
      setAplicar(null); setUpeSel("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const abrirEditor = (c, nivel) => setEditor(c
    ? { ...c, requisitosTxt: c.requisitos.join("\n") }
    : { nombre: "", autoridad: "", nivel, dependencia: "", nota: "", prioridad: "Media", requisitosTxt: "" });

  const guardarPermiso = async () => {
    if (!editor.nombre.trim()) return toast.error("El nombre del permiso es obligatorio");
    setSaving(true);
    const body = {
      nombre: editor.nombre, autoridad: editor.autoridad, nivel: editor.nivel,
      dependencia: editor.dependencia, nota: editor.nota, prioridad: editor.prioridad,
      requisitos: editor.requisitosTxt.split("\n").map((r) => r.trim()).filter(Boolean),
    };
    try {
      if (editor.id) await api.patch(`/catalogo/${editor.id}`, body);
      else await api.post("/catalogo", body);
      toast.success(editor.id ? "Permiso actualizado en el catálogo" : "Permiso agregado al catálogo");
      setEditor(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const eliminarPermiso = async () => {
    try {
      await api.delete(`/catalogo/${editor.id}`);
      toast.success("Permiso eliminado del catálogo (los trámites ya instanciados se conservan)");
      setEditor(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!catalogo) return <div className="p-10 text-slate-400 animate-pulse">Cargando catálogo...</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Catálogo maestro editable</p>
        <h1 className="font-display text-3xl font-bold text-[#002B49]">Catálogo de Permisología</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Al activar la permisología de una UPE, el gestor selecciona los permisos que aplican. Cada permiso incluye sus requisitos como sub-checklist.</p>
      </motion.div>

      {/* Semáforo por autoridad */}
      <Card className="p-5 mb-6" testId="semaforo-autoridades">
        <SectionTitle>Semáforo por autoridad (cuellos de botella)</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {Object.entries(semaforoAutoridad).map(([a, s]) => {
            const color = s.detenidos > 0 ? "bg-red-50 text-red-700 border-red-200" :
              s.aprobados === s.total ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
            return <span key={a} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${color}`}>
              {a} · {s.aprobados}/{s.total} aprobados{s.detenidos > 0 ? ` · ${s.detenidos} detenidos` : ""}
            </span>;
          })}
          {Object.keys(semaforoAutoridad).length === 0 && <p className="text-sm text-slate-400">Aún sin trámites de permisología instanciados.</p>}
        </div>
      </Card>

      <Tabs defaultValue="municipal">
        <TabsList className="bg-white border border-slate-200 h-auto p-1 flex-wrap w-full sm:w-auto justify-start">
          {NIVELES.map((n) => {
            const items = catalogo.filter((c) => c.nivel === n.key);
            const inst = items.filter((c) => instanciados[c.id]?.length).length;
            return (
              <TabsTrigger key={n.key} value={n.key} data-testid={`tab-${n.key}`}
                className="data-[state=active]:bg-[#002B49] data-[state=active]:text-white px-5 py-2.5 gap-2">
                <n.icon size={14} /> {n.label}
                <span className="text-[10px] opacity-70">({inst}/{items.length})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {NIVELES.map((n) => (
          <TabsContent key={n.key} value={n.key} className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-slate-400 uppercase tracking-widest">{n.sub}</p>
              {isAdmin && (
                <Button data-testid={`nuevo-permiso-${n.key}`} size="sm" variant="outline"
                  onClick={() => abrirEditor(null, n.key)}
                  className="h-7 text-xs gap-1 border-[#00A3B5] text-[#00A3B5] hover:bg-[#00A3B5]/5 active:scale-[0.97]">
                  <Plus size={12} /> Nuevo permiso {n.label.toLowerCase()}
                </Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {catalogo.filter((c) => c.nivel === n.key).map((c, i) => {
                const inst = instanciados[c.id] || [];
                return (
                  <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}>
                    <Card className="p-5 h-full flex flex-col" testId={`permiso-${c.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="font-display font-bold text-[#002B49] text-sm leading-snug">{c.nombre}</h4>
                        {c.prioridad === "Alta" && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 whitespace-nowrap">Prioridad Alta</span>}
                      </div>
                      <p className="text-xs text-slate-500 mb-1"><span className="font-semibold">Autoridad:</span> {c.autoridad}</p>
                      {c.dependencia && c.dependencia !== "—" && <p className="text-xs text-slate-400 mb-2"><span className="font-semibold">Dependencia:</span> {c.dependencia}</p>}
                      {c.nota && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2">{c.nota}</p>}
                      <ul className="text-xs text-slate-600 space-y-1 mb-3 flex-1">
                        {c.requisitos.map((r) => <li key={r} className="flex gap-1.5"><span className="text-[#00A3B5]">·</span>{r}</li>)}
                      </ul>
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        {inst.length > 0 ? (
                          <span className="text-xs text-emerald-700 flex items-center gap-1 font-semibold">
                            <CircleCheck size={13} /> Instanciado en {inst.length} UPE{inst.length > 1 ? "s" : ""}
                          </span>
                        ) : <span className="text-xs text-slate-400">Sin instanciar</span>}
                        <div className="flex items-center gap-1.5">
                          {isAdmin && (
                            <button data-testid={`editar-permiso-${c.id}`} onClick={() => abrirEditor(c, n.key)}
                              className="text-slate-300 hover:text-[#00A3B5] transition-colors p-1" title="Editar permiso">
                              <Pencil size={13} />
                            </button>
                          )}
                          {canEdit && (
                            <Button data-testid={`aplicar-permiso-${c.id}`} size="sm" variant="outline"
                              onClick={() => setAplicar(c)}
                              className="h-7 text-xs gap-1 border-[#00A3B5] text-[#00A3B5] hover:bg-[#00A3B5]/5">
                              <CirclePlus size={12} /> Aplicar a UPE
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!aplicar} onOpenChange={(o) => !o && setAplicar(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Aplicar "{aplicar?.nombre}" a una UPE</DialogTitle></DialogHeader>
          <Select value={upeSel} onValueChange={setUpeSel}>
            <SelectTrigger data-testid="upe-destino-select"><SelectValue placeholder="Selecciona la UPE destino" /></SelectTrigger>
            <SelectContent>
              {upes.map((u) => <SelectItem key={u.id} value={u.id}>{u.clave} — {u.uso}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="confirmar-aplicar-btn" onClick={aplicarUpe} className="bg-[#002B49] text-white w-full">Instanciar permiso con sub-checklist</Button>
        </DialogContent>
      </Dialog>

      {/* Editor del catálogo (Admin) */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display text-[#002B49]">
              {editor?.id ? "Editar permiso del catálogo" : "Nuevo permiso del catálogo"}
            </DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <Input data-testid="permiso-nombre-input" placeholder="Nombre del permiso" value={editor.nombre}
                onChange={(e) => setEditor({ ...editor, nombre: e.target.value })} />
              <Input data-testid="permiso-autoridad-input" placeholder="Autoridad emisora" value={editor.autoridad}
                onChange={(e) => setEditor({ ...editor, autoridad: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Select value={editor.nivel} onValueChange={(v) => setEditor({ ...editor, nivel: v })}>
                  <SelectTrigger data-testid="permiso-nivel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="municipal">Municipal</SelectItem>
                    <SelectItem value="estatal">Estatal</SelectItem>
                    <SelectItem value="federal">Federal</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={editor.prioridad} onValueChange={(v) => setEditor({ ...editor, prioridad: v })}>
                  <SelectTrigger data-testid="permiso-prioridad-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Alta", "Media", "Baja"].map((p) => <SelectItem key={p} value={p}>Prioridad {p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input data-testid="permiso-dependencia-input" placeholder="Dependencia (texto)" value={editor.dependencia}
                onChange={(e) => setEditor({ ...editor, dependencia: e.target.value })} />
              <Textarea data-testid="permiso-nota-input" placeholder="Nota / advertencia (opcional)" rows={2} value={editor.nota}
                onChange={(e) => setEditor({ ...editor, nota: e.target.value })} />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Requisitos (uno por línea)</p>
                <Textarea data-testid="permiso-requisitos-input" rows={6} value={editor.requisitosTxt}
                  onChange={(e) => setEditor({ ...editor, requisitosTxt: e.target.value })} />
              </div>
              <p className="text-xs text-slate-400">Los cambios aplican a futuras instancias; los trámites ya instanciados en UPEs no se modifican.</p>
              <div className="flex gap-2">
                {editor.id && (
                  <Button data-testid="eliminar-permiso-btn" variant="outline" onClick={eliminarPermiso}
                    className="border-red-200 text-red-600 hover:bg-red-50 gap-1"><Trash2 size={13} /> Eliminar</Button>
                )}
                <Button data-testid="guardar-permiso-btn" onClick={guardarPermiso} disabled={saving}
                  className="flex-1 bg-[#002B49] text-white active:scale-[0.98]">{saving ? "Guardando..." : "Guardar permiso"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
